/**
 * `sibyl review <pr>` — the wiring, and nothing else.
 *
 * ⛔ THIS FILE MAKES NO JUDGEMENTS. It resolves a credential, picks a backend, opens a store, and
 * hands the pieces to `review()` in src/engine/run.ts. Every rule about what a finding is, what
 * drops it and what may be posted lives behind that call, where it is tested against fabricated
 * artifacts. A condition written here would be a rule with no test.
 *
 * ⛔ EXIT 0 FOR A REVIEW THAT FOUND DEFECTS. The exit code answers "did the harness work", never
 * "was the code clean" — see the header of src/cli/index.ts for the defect that rule comes from.
 * A stage that genuinely broke exits 69, because that is a backend or environment problem and not a
 * configuration one.
 */

import { fileURLToPath } from "node:url"
import type { SibylConfig } from "../config/schema.js"
import { EXIT } from "../config/validate.js"
import { AnthropicApiBackend } from "../backend/anthropic-api.js"
import { ClaudeCliBackend } from "../backend/claude-cli.js"
import { bannerLine, resolveBackend, stampDegradation } from "../backend/probe.js"
import type { Backend } from "../backend/types.js"
import { BackendError } from "../backend/types.js"
import { GitHub } from "../github/pr.js"
import { prepareClone } from "../repo/clone.js"
import { openTriageStore } from "../triage/store.js"
import { renderRun, review, type ReviewDeps } from "../engine/run.js"
import { describeMissing, loadCredentialStore, resolveCredential } from "../config/secrets.js"
import { licenceRefusal } from "../licence/gate.js"

/** Where the shipped prompt files live, relative to the built output. */
export const PROMPT_ROOT = fileURLToPath(new URL("../../", import.meta.url))

/**
 * First key present wins, in the configured order.
 *
 * ⚠️ Absence from `process.env` is not absence: config.md lists a credential store and an OAuth
 * profile after the environment. This resolves only what it can see and says which name it used, so
 * a missing credential names the variable rather than reporting a generic auth failure.
 */
export function credentialFrom(
  names: readonly string[],
  env: NodeJS.ProcessEnv,
  store: ReadonlyMap<string, string> = new Map(),
): { name: string; value: string } | undefined {
  // ⛔ Delegates rather than re-implementing. This function used to read `process.env` and nothing
  // else, which is why a key written to the documented `secrets.store` path was silently ignored.
  return resolveCredential(names, env, store)
}

export interface ReviewCommandOptions {
  readonly repo?: string
  readonly pr: number
  readonly config: SibylConfig
  readonly env?: NodeJS.ProcessEnv
  readonly out?: (s: string) => void
  readonly err?: (s: string) => void
}

/**
 * Build the live backend the resolution chose.
 *
 * ⛔ `stampDegradation` wraps it when a fallback happened, so every finding carries what ACTUALLY
 * ran. A degraded review presented as a full one is the failure the probe exists to prevent.
 */
export function backendFor(
  cfg: SibylConfig,
  kind: string,
  degradedFrom: string | undefined,
  env: NodeJS.ProcessEnv,
  store: ReadonlyMap<string, string> = new Map(),
): Backend {
  let inner: Backend
  if (kind === "anthropic_api") {
    const cred = credentialFrom([cfg.backend.anthropicApi.apiKeyName], env, store)
    if (cred === undefined) {
      throw new BackendError(
        `the Anthropic API was selected but ${cfg.backend.anthropicApi.apiKeyName} was not found in the environment or the credential store`,
        "anthropic_api",
      )
    }
    inner = new AnthropicApiBackend({ config: cfg.backend.anthropicApi, apiKey: cred.value })
  } else {
    inner = new ClaudeCliBackend({ config: cfg.backend.claudeCli, env })
  }
  return degradedFrom === undefined ? inner : stampDegradation(inner, degradedFrom as "claude_cli" | "anthropic_api")
}

export async function runReviewCommand(opts: ReviewCommandOptions): Promise<number> {
  const env = opts.env ?? process.env
  const out = opts.out ?? ((s: string) => process.stdout.write(s))
  const err = opts.err ?? ((s: string) => process.stderr.write(s))
  const cfg = opts.config

  const repo = opts.repo ?? cfg.repo.slug
  if (repo === "") {
    err("⛔ no repository. Pass `--repo owner/name` or set `slug` under [repo].\n")
    return EXIT.USAGE
  }

  const secrets = loadCredentialStore(cfg.secrets.store)
  const githubNames = cfg.secrets.keys.filter((k) => k.includes("GITHUB") || k.includes("GH_"))
  const token = credentialFrom(githubNames, env, secrets)
  if (token === undefined) {
    err(`⛔ ${describeMissing(githubNames, cfg.secrets.store)}\n`)
    return EXIT.UNAVAILABLE
  }

  // Licence gate (src/licence/gate.ts): public repositories are free, private ones need a key.
  const refusal = await licenceRefusal(repo, token.value, { ...Object.fromEntries(secrets), ...env })
  if (refusal !== undefined) {
    err(`${refusal}\n`)
    return EXIT.CONFIG
  }

  // ⛔ The probe reads the environment directly, so a key that lives only in the store would make it
  // report the API as absent and refuse a backend that is in fact available. The store is merged in
  // for the probe's benefit, on a COPY — `process.env` itself is never mutated, because that would
  // leak the value to every child this process later spawns.
  const envForProbe: NodeJS.ProcessEnv = { ...env }
  for (const [k, v] of secrets) if (envForProbe[k] === undefined) envForProbe[k] = v

  const resolution = await resolveBackend(cfg, { lookupCredential: (name: string) => envForProbe[name] })
  err(`${bannerLine(resolution)}\n`)
  if (resolution.backend === "none") {
    // ⛔ Refused rather than run empty. A review with no model that reports zero findings is
    // indistinguishable from a clean review, and that is the worst output this tool could produce.
    err("⛔ no usable backend, so this would report zero findings over code nothing read.\n")
    for (const c of resolution.checks) err(`   ${c.name}: ${c.status} — ${c.detail}\n`)
    return EXIT.UNAVAILABLE
  }

  const backend = backendFor(cfg, resolution.backend, resolution.degradedFrom, envForProbe, secrets)
  const github = new GitHub({ token: token.value })
  const store = await openTriageStore({ path: cfg.triage.db })
  err(`triage: ${store.backend.kind} at ${store.backend.path} — ${store.backend.reason}\n`)

  const deps: ReviewDeps = {
    pullRequest: (r, n) => github.pullRequest(r, n),
    changedFiles: (r, n) => github.changedFiles(r, n),
    // ⭐ The same token the API reads with. Without it a private repository cannot be cloned,
    // which is every repository this tool was built for.
    prepareClone: (r, n) => prepareClone(cfg, r, n, token.value),
    backend,
    promptRoot: PROMPT_ROOT,
    upsert: (findings, sha) => {
      const r = store.upsert(findings, { sha })
      return { created: r.created.length, carriedForward: r.carriedForward.length }
    },
    isDismissed: (fp) => store.isDismissed(fp),
    log: (line) => err(`   ${line}\n`),
  }

  try {
    const run = await review({ repo, pr: opts.pr, config: cfg, deps })
    out(`${renderRun(run)}\n`)
    out(`\nNothing has been posted. Review the queue with \`sibyl triage\`, or open \`sibyl web\`.\n`)
    // ⛔ Findings are not an error. Only a broken stage is.
    return run.failures.length > 0 ? EXIT.UNAVAILABLE : EXIT.OK
  } finally {
    store.close()
  }
}
